/**
 * Parses a multi-language text string into an object with language codes as keys.
 * 
 * @param input - The input string in the format "[lang] text [/lang]"
 * @returns An object with language codes as keys and corresponding texts as values
 */
export function parseMultiLangText(input: string): Record<string, string> {
  const langRegex = /\[([a-z]{2}(?:-[a-zA-Z]{2})?)\]([\s\S]*?)\[\/\1\]/gi;
  const result: Record<string, string> = {};
  let match: RegExpExecArray | null;

  while ((match = langRegex.exec(input)) !== null) {
    const [, lang, text] = match;
    result[lang.toLowerCase()] = text.trim();
  }

  // If no languages were found, treat the entire input as English
  if (Object.keys(result).length === 0) {
    result['en'] = input.trim();
  }

  return result;
}

/**
 * Retrieves the text for a specific language from a parsed multi-language object.
 * Falls back to English if the requested language is not available.
 * 
 * @param translations - The parsed multi-language object
 * @param lang - The desired language code
 * @returns The text in the requested language, or English as fallback
 */
export function getTextForLanguage(translations: Record<string, string>, lang: string): string {
  // Normalize the requested language code to lowercase
  const normalizedLang = lang.toLowerCase();
  
  // Try exact match first
  if (translations[normalizedLang]) {
    return translations[normalizedLang];
  }

  // Try matching just the language part (e.g., 'es' for 'es-MX')
  const baseLang = normalizedLang.split('-')[0];
  const matchingKey = Object.keys(translations).find(key => 
    key.toLowerCase() === normalizedLang || key.toLowerCase().startsWith(baseLang + '-')
  );

  if (matchingKey) {
    return translations[matchingKey];
  }

  // Fallback to English or first available translation
  return translations['en'] || Object.values(translations)[0] || '';
}

/**
 * Retrieves the language code from the URL parameters.
 * Defaults to 'en' if no language parameter is found.
 * 
 * @returns The language code as a string
 */
export function getLangFromUrlParams(): string {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('lang') || 'en';
}

/**
 * Parses and localizes multi-language text based on the current language.
 * Supports interpolation of values.
 * 
 * @param text - The multi-language text to localize
 * @param currentLang - The current language code
 * @param interpolations - An object containing key-value pairs for interpolation
 * @returns The localized text for the current language with interpolated values
 */
export function localizeText(text: string, currentLang: string, interpolations?: Record<string, string | number>): string {
  if (typeof text !== 'string') {
    // console.error('Expected a string for text, but received:', text);
    return ''; // Return an empty string or a default value
  }

  const parsed = parseMultiLangText(text);
  let localizedText = getTextForLanguage(parsed, currentLang);

  if (interpolations) {
    Object.entries(interpolations).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      localizedText = localizedText.replace(regex, String(value));
    });
  }

  return localizedText;
}

export const getUrlParams = (key?: string): Record<string, string> | string | null => {
  const currentUrl = window.location.href;
  const urlParams = new URLSearchParams(new URL(currentUrl).search);

  if (key) {
    return urlParams.get(key); // Returns the value for the specific key or null if not found
  }

  const paramsObject: Record<string, string> = {};
  urlParams.forEach((value, key) => {
    paramsObject[key] = value;
  });

  return paramsObject; // Returns all parameters as an object
};

